<template>
    <div class="cart-item" :class="{ 'has-discount': item.hasDiscount, disabled: !isSelectable }" @click="onClick">
        <QuantitySelectionCheckbox
            v-if="isSelectable"
            :selectedQuantity="isSelected ? 1 : 0"
            :totalQuantity="1"
            :visible="true"
        />

        <OrderItemQuantity class="quantity" :quantity="item.quantity" :quantityDigits="quantityDigits" :indent="true" />

        <div class="item-details-container">
            <div class="main-details">
                <span class="name">{{ getNameInLocationLanguage(item, props.showInternalNames) }}<ItemIcons :articleId="item.articleId" /></span>
                <div class="increaser" v-if="isChangeQuantityAvailable" @click.stop>
                    <button
                        class="btn decrease"
                        ref="decreaseBtn"
                        :disabled="!isDecreaseAvailable"
                        @click="decreaseQuantity"
                    >
                        <div class="outer"><i class="fas fa-minus-circle"></i></div>
                    </button>
                    <button
                        class="btn increase"
                        ref="increaseBtn"
                        :disabled="!isIncreaseAvailable"
                        @click="increaseQuantity"
                    >
                        <div class="outer"><i class="fas fa-plus-circle"></i></div>
                    </button>
                </div>
                <CartItemPrice class="price" :item="item" @click="onPriceClick" />
                <button
                    class="remover"
                    ref="removerBtn"
                    type="button"
                    @click.stop="removeItem"
                    :disabled="!isRemoveAvailable"
                >
                    <div class="outer"><i class="fal fa-trash-alt"></i></div>
                </button>
            </div>

            <div class="subs" v-if="item.options.length">
                <CartItemOptionGroup
                    v-for="optionGroup in item.options"
                    :key="optionGroup.id"
                    :optionGroup="optionGroup"
                    :multiplier="item.quantity"
                    :showInternalNames="props.showInternalNames"
                />
            </div>

            <CartItemCommentEditor
                @click.stop
                :item="item"
                :disabled="props.disabled"
                :isOperatorUser="identity.hasOperatorAccess"
                :areItemCommentsAllowed="cart.areItemCommentsAllowed"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { ICartItem } from "@/data/model/Customer";
    import { useCartStore } from "@/data/stores/cart";
    import { useIdentityStore } from "@/data/stores/identity";
    import { useOfferStore } from "@/data/stores/offer";
    import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
    import { Ripple } from "@/shared/ui/Ripple";
    import { computed, onMounted, ref } from "vue";

    import CartItemCommentEditor from "@/components/cart/CartItemCommentEditor.vue";
    import CartItemOptionGroup from "@/components/cart/CartItemOptionGroup.vue";
    import CartItemPrice from "@/components/cart/CartItemPrice.vue";
    import QuantitySelectionCheckbox from "@/components/elements/QuantitySelectionCheckbox.vue";
    import ItemIcons from "@/components/order/ItemIcons.vue";
    import OrderItemQuantity from "@/components/order/OrderItemQuantity.vue";

    const cart = useCartStore();
    const identity = useIdentityStore();
    const offer = useOfferStore();

    // define properties
    const props = defineProps<{
        item: ICartItem;
        quantityDigits?: number;
        disabled: boolean;
        showInternalNames: boolean;
    }>();

    // define computed
    const cartItemMinQuantity = computed(() => props.item.minQuantity ?? 1);
    const cartItemMaxQuantity = computed(() => {
        const maxTotalPerArticle = props.item.maxQuantity ?? Number.MAX_VALUE;
        const maxTotalUnitsPerOrder = cart.orderService?.useCase?.maximumTotalUnitsPerOrder ?? Number.MAX_VALUE;
        const remainingUnitsInOrder = maxTotalUnitsPerOrder - (cart.totalCartMainItems - props.item.quantity);
        return Math.min(maxTotalPerArticle, remainingUnitsInOrder);
    });

    // selecting cart items only feeds the discount button, so it is pointless without discounts
    const isSelectable = computed(() => !props.disabled && offer.areDiscountsEnabled);
    const isSelected = computed(() => cart.isCartItemSelected(props.item));
    const isChangeQuantityAvailable = computed(() => cartItemMaxQuantity.value != cartItemMinQuantity.value);
    const isDecreaseAvailable = computed(() => props.item.quantity > cartItemMinQuantity.value);
    const isIncreaseAvailable = computed(() => props.item.quantity < cartItemMaxQuantity.value);
    const isRemoveAvailable = computed(() => true);

    // define methods
    const decreaseQuantity = () => cart.decreaseCartItemQuantity(props.item);
    const increaseQuantity = () => cart.increaseCartItemQuantity(props.item);
    const removeItem = () => cart.removeCartItem(props.item);

    // define html refs
    const decreaseButton = ref<HTMLElement | null>(null);
    const increaseButton = ref<HTMLElement | null>(null);
    const removeButton = ref<HTMLElement | null>(null);

    // functions
    function onClick() {
        if (!isSelectable.value) return;

        cart.toggleCartItemSelection(props.item);
    }

    function onPriceClick(event: MouseEvent) {
        if (!props.item.hasDiscount) {
            return;
        }

        // without a discount to remove the click falls through to the row and changes the selection
        event.stopPropagation();
        cart.showConfirmItemDiscountsRemovalPopup(props.item);
    }

    // define onMounted
    onMounted(() => {
        Ripple.bindRippleOut($(decreaseButton.value as any));
        Ripple.bindRippleOut($(increaseButton.value as any));
        Ripple.bindRippleOut($(removeButton.value as any));
    });
</script>

<style scoped lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .cart-item {
        font-size: 16px;
        padding: 1.25em 0;
        position: relative;
        border-bottom: 1px solid colors.$lightGreyBorder;
        font-weight: 300;
        line-height: 1.25em;
        color: colors.$fontColor;
        display: flex;
        position: relative;

        &:not(.disabled) {
            cursor: pointer;
        }

        &.has-discount {
            padding-top: 1.725em;

            .main-details {
                margin-bottom: -0.625em;

                > .price {
                    cursor: pointer;
                    transition: opacity 0.2s variables.$easeOutExpo;

                    &:hover {
                        opacity: 0.5;
                    }
                }
            }
        }

        > .item-details-container {
            flex: 1 1 auto;
            color: colors.$fontColor;
            transition: color 0.2s variables.$easeOutExpo;

            &.inactive {
                color: colors.$lightFontColor;
            }

            > .main-details {
                display: flex;
                align-items: flex-start;

                .name {
                    text-overflow: ellipsis;
                    flex: 1 1 auto;
                    word-break: break-word;
                }

                > .increaser {
                    flex: 0 0 52px;
                    font-size: 0;
                    white-space: nowrap;

                    > .btn {
                        display: inline-block;
                        vertical-align: top;
                        background-color: transparent;
                        border: none;
                        padding: 4px;
                        margin: -4px 0;

                        > .outer {
                            height: 18px;
                            width: 18px;
                            border-radius: 50%;
                            text-align: center;

                            > .fas {
                                transition: color 0.2s variables.$easeOutExpo;
                                line-height: 18px;
                                font-size: 18px;
                            }
                        }

                        &.decrease {
                            > .outer > .fas {
                                color: colors.$orange;
                            }
                        }

                        &.increase {
                            > .outer > .fas {
                                color: colors.$green;
                            }
                        }

                        &:disabled {
                            cursor: default !important;

                            > .outer > .fas {
                                color: #cccccc !important;
                            }
                        }
                    }
                }

                .price {
                    flex: 0 0 3.75em;

                    &.has-discount {
                        position: relative;
                        top: -0.625em;
                    }
                }

                > .remover {
                    flex: 0 0 20px;
                    margin: -10px;
                    padding: 10px;
                    text-align: right;
                    cursor: pointer;
                    font-size: 0;
                    background-color: colors.$white;
                    margin-left: 0;

                    > .outer {
                        display: inline-block;
                        vertical-align: top;
                        height: 12px;

                        > .fal {
                            line-height: 20px;
                            font-size: 12px;
                            color: colors.$lightFontColor;
                        }
                    }

                    &:disabled {
                        cursor: default;

                        .fal {
                            color: #ddd !important;
                        }
                    }
                }
            }

            > .subs {
                padding: 0.375em 0.125em 0.5em;
                margin-bottom: -0.75em;
            }
        }
    }
</style>
